// CORE
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Toast from 'Components/Commons/Toast/Toast';
import { hideToast } from 'Redux/General/Toast';

/**
 * @component
 * @description Global event manager component that handles application-wide events and notifications.
 * This component manages toast notifications by connecting to the Redux store and providing
 * a centralized way to display user feedback messages throughout the application.
 *
 * Features:
 * - Centralized toast notification management
 * - Redux state integration for toast messages
 * - Automatic toast hiding functionality
 * - Success/error message differentiation
 *
 * @returns {JSX.Element} Toast notification system
 *
 * @example
 * // Used in Website.js
 * import EventManager from './EventManager/EventManager';
 *
 * function Website() {
 *   return (
 *     <React.Fragment>
 *       <ContentWrapper>
 *         <Routes />
 *       </ContentWrapper>
 *       <EventManager />
 *     </React.Fragment>
 *   );
 * }
 *
 * // To show a toast from any component:
 * import { useDispatch } from 'react-redux';
 * import { showToast } from 'Redux/General/Toast';
 *
 * function SomeComponent() {
 *   const dispatch = useDispatch();
 *
 *   const handleSuccess = () => {
 *     dispatch(showToast({
 *       message: 'Operation successful!',
 *       success: true
 *     }));
 *   };
 * }
 *
 * @see Toast - Toast notification component
 * @see hideToast - Redux action to hide toast
 * @see showToast - Redux action to show toast
 */
const EventManager = () => {
    /**
     * @constant
     * @description Toast state from Redux store containing notification data.
     *
     * @type {Object}
     * @property {boolean} enable - Whether toast is currently visible
     * @property {string} message - Toast message content
     * @property {boolean} success - Whether toast represents success or error
     */
    const toast = useSelector((state) => state.Toast);

    /**
     * @constant
     * @description Redux dispatch function for triggering actions.
     *
     * @type {Function}
     */
    const dispatch = useDispatch();

    return (
        <React.Fragment>
            <Toast
                open={toast.enable}
                success={toast.success}
                message={toast.message}
                handleClose={() => dispatch(hideToast())}
            />
        </React.Fragment>
    );
};

export default EventManager;
